import { chain, isEqual, flatten } from "lodash";
import React from "react";
import { useRouteMatch } from "react-router-dom";
import type { ActionEvent, AnalyticActionDispatcher, AnalyticErrorCallback, AnalyticTrackedActionDispatcher } from "~/analytics/Analytics";
import { Action, Action as AnalyticsAction, useAnalyticActionDispatch, useAnalyticTrackedActionDispatch } from "~/analytics/Analytics";
import FailureMode from "~/areas/projects/components/Releases/Deployments/FailureMode";
import { DeploymentModelType } from "~/areas/projects/components/Runbooks/RunbookRunNowLayout";
import type { WithProjectContextInjectedProps } from "~/areas/projects/context";
import { useProjectContext } from "~/areas/projects/context";
import ProjectContextRepository from "~/client/repositories/projectContextRepository";
import type { DeploymentSettingsResource, DeploymentProcessResource, GuidedFailureMode, ProjectResource, ModifyDeploymentSettingsCommand, VcsBranchResource } from "~/client/resources";
import { Permission } from "~/client/resources";
import type { DeploymentActionPackageResource } from "~/client/resources/deploymentActionPackageResource";
import { deploymentActionPackages, displayName } from "~/client/resources/deploymentActionPackageResource";
import { client, repository } from "~/clientInstance";
import { RoleChip } from "~/components/Chips";
import type { OptionalFormBaseComponentState } from "~/components/FormBaseComponent";
import FormBaseComponent from "~/components/FormBaseComponent";
import { RoleMultiSelect } from "~/components/MultiSelect/RoleMultiSelect";
import ExternalLink from "~/components/Navigation/ExternalLink";
import { OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import type { MenuItem } from "~/components/OverflowMenu/OverflowMenu";
import type { PermissionCheckProps } from "~/components/PermissionCheck/PermissionCheck";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import type { SummaryNode } from "~/components/form";
import { BooleanRadioButtonGroup, ExpandableFormSection, MarkdownEditor, Note, RadioButton, StringRadioButtonGroup, Summary, UnstructuredFormSection } from "~/components/form";
import Callout, { CalloutType } from "~/primitiveComponents/dataDisplay/Callout/Callout";
import routeLinks from "~/routeLinks";
import { noOp } from "~/utils/noOp";
import type { FormPaperLayoutProps } from "../../../../components/FormPaperLayout";
import { isVersionControlledProcess } from "../Process/Common/CommonProcessHelpers";
import DeploymentSettingsOclEditorDialogTrigger from "../Process/DeploymentSettingsOclEditorDialogTrigger/DeploymentSettingsOclEditorDialogTrigger";
import type { GetCommitButtonProps } from "../VersionControl/CommitButton";
import { GetCommitButton } from "../VersionControl/CommitButton";
import type { CommitMessageWithDetails } from "../VersionControl/CommitMessageWithDetails";
import { getFormattedCommitMessage } from "../VersionControl/CommitMessageWithDetails";
import { DeploymentSettingsFormPaperLayout, VersionControlledDeploymentSettingsFormPaperLayout } from "./DeploymentProcessSettingsFormPaperLayout";
import { isVersioningConfigurationValid } from "./InvalidConfigurationCallout";
import { ReleaseVersioning } from "./ReleaseVersioning";

export interface DeploymentSettingsModel {
    guidedFailureMode: GuidedFailureMode;
    releaseNotesTemplate: string | undefined;
    skipIfAlreadyInstalled: boolean;
    deploymentChangesTemplate: string | undefined;
    skipMachines: string;
    skipMachinesRoles: string[];
    allowDeploymentsToNoTargets: boolean;
    excludeUnhealthyTargets: boolean;
    versioningStrategyTemplate: string;
    versionFromPackage: boolean;
    versioningStrategyPackage: DeploymentActionPackageResource | undefined;
}

interface DeploymentSettingsState extends OptionalFormBaseComponentState<DeploymentSettingsModel> {
    machineRoles: string[];
    versionPackageActions: DeploymentActionPackageResource[];
    deploymentProcess?: DeploymentProcessResource;
    deploymentSettings?: DeploymentSettingsResource;
    project?: ProjectResource;
    commitMessage: CommitMessageWithDetails;
    disableDirtyFormChecking?: boolean;
}

interface DeploymentSettingsAnalyticsProps {
    dispatchAction: AnalyticActionDispatcher;
    trackAction: AnalyticTrackedActionDispatcher;
}

type MatchProps = { match: NonNullable<ReturnType<typeof useRouteMatch>> | undefined };
type DeploymentSettingsProps = MatchProps & WithProjectContextInjectedProps;
type DeploymentSettingsPropsInternal = DeploymentSettingsProps & DeploymentSettingsAnalyticsProps;

export const defaultReleaseVersioningTemplate: string = "#{Octopus.Version.LastMajor}.#{Octopus.Version.LastMinor}.#{Octopus.Version.NextPatch}";
const defaultCommitMessage = "Update deployment settings";

class DeploymentProcessSettingsInternal extends FormBaseComponent<DeploymentSettingsPropsInternal, DeploymentSettingsState, DeploymentSettingsModel> {
    private openCommitDialog?: () => void;

    constructor(props: DeploymentSettingsPropsInternal) {
        super(props);
        const project = this.props.projectContext.state.model;

        this.state = {
            machineRoles: [],
            versionPackageActions: [],
            project,
            commitMessage: { summary: "", details: "" },
        };
    }

    async componentDidMount() {
        await this.reload();
    }

    async componentDidUpdate(prevProps: DeploymentSettingsProps) {
        if (prevProps.projectContext.state.branch !== this.props.projectContext.state.branch) {
            await this.reload();
        }
    }

    private reload = async () => {
        await this.doBusyTask(async () => {
            const { model: project, projectContextRepository } = this.props.projectContext.state;
            const deploymentSettings = await projectContextRepository.DeploymentSettings.get();
            const hasProcessViewPermissions = isAllowed({
                permission: Permission.ProcessView,
                project: project.Id,
                tenant: "*",
            });
            const [deploymentProcess, machineRoles] = await Promise.all<DeploymentProcessResource | undefined, string[]>([
                hasProcessViewPermissions ? projectContextRepository.DeploymentProcesses.get() : Promise.resolve(undefined),
                repository.MachineRoles.all(),
            ]);

            const versionPackageActions = deploymentActionPackages(
                chain(deploymentProcess && deploymentProcess.Steps)
                    .flatMap((step) => step.Actions)
                    .filter((action) => action.CanBeUsedForProjectVersioning)
                    .value()
            );

            this.setState({
                versionPackageActions,
                machineRoles,
                deploymentProcess,
                deploymentSettings,
                model: this.buildModel(deploymentSettings),
                cleanModel: this.buildModel(deploymentSettings),
            });
        });
    };

    render() {
        const isVersionControlled = this.state.project?.IsVersionControlled ?? false;

        const innerLayout = this.state.model && this.state.deploymentSettings && this.state.project && (
            <>
                {displayCalloutIfVersioningConfigurationInvalid(this.state.project, this.state.deploymentSettings, this.state.deploymentProcess)}

                <ExpandableFormSection errorKey="versionFromPackage" title="Release Versioning" summary={this.releaseVersioningSummary()} help="Select how the next release number is generated when creating a release.">
                    <ReleaseVersioning
                        project={this.state.project}
                        deploymentProcess={this.state.deploymentProcess}
                        versionFromPackage={this.state.model.versionFromPackage}
                        versioningStrategyPackage={this.state.model.versioningStrategyPackage}
                        versioningStrategyTemplate={this.state.model.versioningStrategyTemplate}
                        versionPackageActions={this.state.versionPackageActions}
                        setModelProp={(m) => this.setModelState(m)}
                    />
                </ExpandableFormSection>

                <ExpandableFormSection
                    errorKey="releaseNotesTemplate"
                    title="Release Notes Template"
                    summary={this.state.model.releaseNotesTemplate ? Summary.summary("A release notes template has been specified") : Summary.placeholder("No release notes template provided")}
                    help={this.buildReleaseNotesTemplateHelpInfo()}
                >
                    <MarkdownEditor value={this.state.model.releaseNotesTemplate} accessibleName="Release Notes Template" onChange={(releaseNotesTemplate) => this.setChildState1("model", { releaseNotesTemplate })} />
                    <Note>
                        A release notes template is a convenient way to keep release notes consistent and avoid entering the same text repeatedly. The template generates default release notes, which can be modified on the create release page if
                        desired. See the <ExternalLink href="ReleaseNotes">documentation</ExternalLink> for more information.
                    </Note>
                </ExpandableFormSection>

                <ExpandableFormSection errorKey="allowDeploymentsToNoTargets" title="Deployment Targets Required" summary={this.deploymentTargetsSummary()} help="Choose if deployments are allowed if there are no deployment targets.">
                    <BooleanRadioButtonGroup accessibleName="deployment targets allowed" value={this.state.model.allowDeploymentsToNoTargets} onChange={(allowDeploymentsToNoTargets) => this.setModelState({ allowDeploymentsToNoTargets })}>
                        <RadioButton value={true} label="Allow deployments to be created when there are no deployment targets" isDefault={true} />
                        <RadioButton value={false} label="Deployments with no targets are not allowed" />
                    </BooleanRadioButtonGroup>
                    <Note>
                        In many deployment scenarios, <ExternalLink href="DynamicInfrastructure">infrastructure is created dynamically</ExternalLink> as part of the deployment. Octopus defaults to allowing deployments to start when there are no
                        deployment targets registered for this reason. To make deployment creation fail if no deployment targets are available, select Deployments with no targets are not allowed.
                    </Note>
                </ExpandableFormSection>

                <ExpandableFormSection errorKey="skipMachines" title="Transient Deployment Targets" summary={this.skipMachinesSummary()} help="Choose to skip unavailable, or exclude unhealthy targets from the deployment.">
                    <StringRadioButtonGroup accessibleName="unavailable deployment targets" label="Unavailable Deployment targets" value={this.state.model.skipMachines} onChange={this.handleSkipMachinesChanged}>
                        <RadioButton value="None" label="Fail deployment" isDefault={true} />
                        <RadioButton value="SkipUnavailableMachines" label="Skip and continue" />
                        <Note>Deployment targets that are unavailable at the start of the deployment or become unavailable during the deployment will be skipped and removed from the deployment.</Note>
                    </StringRadioButtonGroup>
                    <Note>
                        <ExternalLink href="DynamicInfrastructure">Read more</ExternalLink> about deploying to elastic and transient environments.
                    </Note>
                    {this.state.model.skipMachines === "SkipUnavailableMachines" && (
                        <>
                            <RoleMultiSelect
                                onChange={(skipMachinesRoles) => this.setModelState({ skipMachinesRoles })}
                                value={this.state.model.skipMachinesRoles}
                                label="Skip unavailable deployment targets only in selected roles"
                                accessibleName="Roles to skip unavailable deployment targets"
                                items={this.state.machineRoles}
                            />
                            <Note>By default, deployment targets will be skipped if they are unavailable in all roles, to limit to certain roles select them here.</Note>
                        </>
                    )}
                    <StringRadioButtonGroup
                        accessibleName="unhealthy deployment targets"
                        value={this.state.model.excludeUnhealthyTargets ? "ExcludeUnhealthy" : "None"}
                        onChange={(skipUnhealthyTargets) => this.setModelState({ excludeUnhealthyTargets: skipUnhealthyTargets === "ExcludeUnhealthy" })}
                        label="Unhealthy Deployment Targets"
                    >
                        <RadioButton value="None" label="Do not exclude" isDefault={true} />
                        <RadioButton value="ExcludeUnhealthy" label="Exclude" />
                        <Note>Deployment targets that are unhealthy at the start of the deployment will be skipped and removed from the deployment.</Note>
                    </StringRadioButtonGroup>
                </ExpandableFormSection>

                <ExpandableFormSection
                    errorKey="deploymentChangesTemplate"
                    title="Deployment Changes Template"
                    summary={this.state.model.deploymentChangesTemplate ? Summary.summary("A deployment changes template has been specified") : Summary.placeholder("No deployment changes template provided")}
                    help={this.buildDeploymentChangesTemplateHelpInfo()}
                >
                    <MarkdownEditor value={this.state.model.deploymentChangesTemplate} label="Deployment changes template" onChange={(deploymentChangesTemplate) => this.setChildState1("model", { deploymentChangesTemplate })} />
                </ExpandableFormSection>

                <FailureMode guidedFailureMode={this.state.model.guidedFailureMode} onModeChanged={(guidedFailureMode) => this.setModelState({ guidedFailureMode })} title="Default Failure Mode" modelType={DeploymentModelType.Deployment} />
            </>
        );

        const overflowMenuItems: Array<MenuItem | MenuItem[]> = this.state.deploymentSettings
            ? [
                  OverflowMenuItems.navItem("Audit Trail", routeLinks.configuration.eventsForProjectDeploymentSettings(this.state.deploymentSettings.ProjectId), {
                      permission: Permission.EventView,
                      wildcard: true,
                  }),
              ]
            : [];

        const project = this.props.projectContext.state.model;
        if (project.IsVersionControlled && this.state.model && this.state.deploymentSettings) {
            const process = this.state.model;
            const cleanProcess = this.state.cleanModel;
            const dirty = !isEqual(process, cleanProcess);
            if (!dirty) {
                overflowMenuItems.unshift(
                    OverflowMenuItems.dialogItem(
                        "View/Edit OCL",
                        <DeploymentSettingsOclEditorDialogTrigger
                            resource={this.state.deploymentSettings}
                            onSave={async () => {
                                await this.reload();
                            }}
                        />
                    )
                );
            } else {
                overflowMenuItems.unshift(OverflowMenuItems.disabledItem("View/Edit OCL", "Please commit your current changes before viewing the OCL"));
            }
        }

        const formPaperLayoutProps: FormPaperLayoutProps = {
            busy: this.state.busy,
            errors: this.errors,
            model: this.state.model,
            cleanModel: this.state.cleanModel,
            onSaveClick: this.handleSaveClick,
            savePermission: this.editPermission(),
            overFlowActions: overflowMenuItems,
            breadcrumbTitle: this.state.project?.Name,
        };

        const onNewBranchCreating = async (branchName: string, source?: string) => {
            this.setState({ disableDirtyFormChecking: true });

            if (this.state.project) {
                const ev: ActionEvent = {
                    action: Action.Commit,
                    resource: "Branch",
                    data: { source: source ?? "Commit changes dialog" },
                };

                let newBranchResource: VcsBranchResource | null = null;

                this.setState({ disableDirtyFormChecking: true });

                await this.props.trackAction("Create branch", ev, async (cb: AnalyticErrorCallback) => {
                    try {
                        newBranchResource = await this.props.projectContext.state.projectContextRepository.Branches.createBranch(this.props.projectContext.state.model, branchName, this.props.projectContext.state.branch?.Name ?? "");
                    } catch (ex) {
                        cb(ex);
                        this.setState({ disableDirtyFormChecking: false });
                        throw ex;
                    }
                });

                if (newBranchResource) {
                    await this.handleSaveClick(false, noOp, newBranchResource);
                    this.props.projectContext.actions.changeBranch(branchName);
                }
            }
            this.setState({ disableDirtyFormChecking: false });
        };

        const commitButtonProps = { ...this.getCommitButtonProps(), onNewBranchCreating };

        if (isVersionControlled) {
            return (
                <VersionControlledDeploymentSettingsFormPaperLayout
                    {...formPaperLayoutProps}
                    disableDirtyFormChecking={this.state.disableDirtyFormChecking}
                    onCreateBranch={(newBranchName) => onNewBranchCreating(newBranchName, "Branch switcher")}
                    customPrimaryAction={(primaryActionProps) => <GetCommitButton {...commitButtonProps} actionButtonProps={primaryActionProps} />}
                >
                    {innerLayout}
                </VersionControlledDeploymentSettingsFormPaperLayout>
            );
        }

        return <DeploymentSettingsFormPaperLayout {...formPaperLayoutProps}>{innerLayout}</DeploymentSettingsFormPaperLayout>;
    }

    private editPermission(): PermissionCheckProps {
        return {
            permission: Permission.ProjectEdit,
            project: this.state.project?.Id,
            tenant: "*",
        };
    }

    buildModel(deploymentSettings: DeploymentSettingsResource): DeploymentSettingsModel {
        if (!deploymentSettings.VersioningStrategy) {
            deploymentSettings.VersioningStrategy = { Template: defaultReleaseVersioningTemplate, DonorPackage: undefined };
        }

        if (!deploymentSettings.ConnectivityPolicy) {
            deploymentSettings.ConnectivityPolicy = {
                SkipMachineBehavior: "None",
                TargetRoles: [],
                AllowDeploymentsToNoTargets: true,
                ExcludeUnhealthyTargets: false,
            };
        }

        return {
            versionFromPackage: !!deploymentSettings.VersioningStrategy.DonorPackage,
            versioningStrategyTemplate: deploymentSettings.VersioningStrategy.Template,
            versioningStrategyPackage: deploymentSettings.VersioningStrategy.DonorPackage,
            skipIfAlreadyInstalled: deploymentSettings.DefaultToSkipIfAlreadyInstalled,
            allowDeploymentsToNoTargets: deploymentSettings.ConnectivityPolicy.AllowDeploymentsToNoTargets,
            skipMachines: deploymentSettings.ConnectivityPolicy.SkipMachineBehavior,
            skipMachinesRoles: deploymentSettings.ConnectivityPolicy.TargetRoles,
            guidedFailureMode: deploymentSettings.DefaultGuidedFailureMode,
            excludeUnhealthyTargets: deploymentSettings.ConnectivityPolicy.ExcludeUnhealthyTargets,
            releaseNotesTemplate: deploymentSettings.ReleaseNotesTemplate,
            deploymentChangesTemplate: deploymentSettings.DeploymentChangesTemplate,
        };
    }

    handleSaveClick = async (isNavigationConfirmation: boolean, onSavedCallback: () => void, newBranch?: VcsBranchResource) => {
        if (isNavigationConfirmation && this.openCommitDialog && isVersionControlledProcess) {
            this.openCommitDialog();
        } else {
            const model = this.state.model;

            if (!model || !this.state.deploymentSettings) {
                throw "no model loaded";
            }

            const settings = { ...this.state.deploymentSettings, Links: { ...this.state.deploymentSettings.Links, Self: newBranch ? newBranch.Links.DeploymentSettings : this.state.deploymentSettings.Links.Self } };

            const commitMessage = getFormattedCommitMessage(this.state.commitMessage, defaultCommitMessage);
            const deploymentSettings: ModifyDeploymentSettingsCommand = {
                ...settings,
                DefaultToSkipIfAlreadyInstalled: model.skipIfAlreadyInstalled,
                VersioningStrategy: {
                    DonorPackage: model.versionFromPackage ? model.versioningStrategyPackage : undefined,
                    Template: model.versionFromPackage ? "" : model.versioningStrategyTemplate,
                },
                ConnectivityPolicy: {
                    AllowDeploymentsToNoTargets: model.allowDeploymentsToNoTargets,
                    SkipMachineBehavior: model.skipMachines,
                    TargetRoles: model.skipMachinesRoles,
                    ExcludeUnhealthyTargets: model.excludeUnhealthyTargets,
                },
                DefaultGuidedFailureMode: model.guidedFailureMode,
                ReleaseNotesTemplate: model.releaseNotesTemplate,
                DeploymentChangesTemplate: model.deploymentChangesTemplate,
                ChangeDescription: commitMessage,
            };

            await this.doBusyTask(async () => {
                const repo = newBranch ? new ProjectContextRepository(client, this.props.projectContext.state.model, newBranch) : this.props.projectContext.state.projectContextRepository;
                const result = await repo.DeploymentSettings.modify(deploymentSettings);

                this.props.dispatchAction("Save Deployment Settings", {
                    resource: "Deployment settings",
                    action: this.state.project?.IsVersionControlled ? AnalyticsAction.Commit : AnalyticsAction.Save,
                    isDefaultBranch: this.props.projectContext.state.isDefaultBranch,
                    commitMessage: this.state.commitMessage.summary.length > 0 ? true : false,
                });

                this.setState(() => {
                    return {
                        model: this.buildModel(result),
                        cleanModel: this.buildModel(result),
                        deploymentSettings: result,
                        disableDirtyFormChecking: false,
                    };
                });
            });

            if (onSavedCallback) {
                onSavedCallback();
            }
        }
    };

    private getCommitButtonProps(): Omit<GetCommitButtonProps, "actionButtonProps"> {
        return {
            project: this.props.projectContext.state.model,
            branch: this.props.projectContext.state.branch,
            defaultCommitMessage: defaultCommitMessage,
            commitMessage: this.state.commitMessage,
            updateCommitMessage: (commitMessage: CommitMessageWithDetails) => this.setState({ commitMessage }),
            commitMessageAccessibleName: "Commit message for saving the deployment settings",
            commitDetailsAccessibleName: "Commit details for saving the deployment settings",
            commitButtonAccessibleName: "Commit changes to the deployment settings",
            onNewBranchCreating: async (branchName: string) => {
                this.setState({ disableDirtyFormChecking: true });

                if (this.state.project) {
                    const newBranchResource = await this.props.projectContext.state.projectContextRepository.Branches.createBranch(this.props.projectContext.state.model, branchName, this.props.projectContext.state.branch?.Name ?? "");
                    await this.handleSaveClick(false, noOp, newBranchResource);
                    this.props.projectContext.actions.changeBranch(branchName);
                    this.setState({ disableDirtyFormChecking: false });
                }
                this.setState({ disableDirtyFormChecking: false });
            },
            onInitializing: (openCommitDialog: () => void) => (this.openCommitDialog = openCommitDialog),
        };
    }

    private handleSkipMachinesChanged = (skipMachines: string) => {
        this.setState((state) => {
            return {
                model: {
                    ...state.model,
                    skipMachines,
                    skipMachinesRoles: skipMachines === "None" ? [] : state.model?.skipMachinesRoles,
                },
            };
        });
    };

    private deploymentTargetsSummary(): SummaryNode {
        return this.state.model?.allowDeploymentsToNoTargets ? Summary.default("Deployments may start without a deployment target") : Summary.summary("A deployment target is required before a deployment may start");
    }

    private skipMachinesSummary(): SummaryNode {
        if (this.state.model?.skipMachines !== "SkipUnavailableMachines") {
            return this.state.model?.excludeUnhealthyTargets ? Summary.summary("Deployment will exclude unhealthy targets, and fail if there is an unavailable target") : Summary.default("Deployment will fail if a deployment target is unavailable");
        }

        const roles = this.state.model.skipMachinesRoles;
        const summary = [this.state.model.excludeUnhealthyTargets ? <span key="skipMachines">Deployment will exclude unhealthy targets, and skip unavailable targets</span> : <span key="skipMachines">Deployment will skip unavailable targets</span>];

        if (roles.length > 0) {
            summary.push(this.state.model.skipMachinesRoles.length > 1 ? <span> in roles</span> : <span> in role</span>);

            roles.forEach((r) => {
                summary.push(<RoleChip role={r} key={"role-" + r} />);
            });
        }
        return Summary.summary(React.Children.toArray(summary));
    }

    private releaseVersioningSummary(): SummaryNode {
        if (this.state.model?.versionFromPackage) {
            const versioningPackage = this.state.model?.versioningStrategyPackage;
            return !!versioningPackage && !!versioningPackage.DeploymentAction
                ? Summary.summary(
                      <span>
                          Based on the package step <strong>{displayName(versioningPackage)}</strong>
                      </span>
                  )
                : Summary.summary("Based on the package in a step, please select a step");
        }
        const template = this.state.model?.versioningStrategyTemplate;
        return Summary.default(
            template ? (
                <span>Based on template {template}</span>
            ) : (
                <span>
                    Based on template, <strong>no template set</strong>
                </span>
            )
        );
    }

    private buildReleaseNotesTemplateHelpInfo(): string {
        return "Enter a template for the release notes that will be used for new releases.";
    }

    private buildDeploymentChangesTemplateHelpInfo() {
        return (
            <span>
                Enter a template for the markdown generated for each deployment's changes. The markdown can be accessed during a deployment using the <code>Octopus.Deployment.ChangesMarkdown</code> variable.{" "}
                <ExternalLink href="DeploymentChangesTemplate">Learn more</ExternalLink>
            </span>
        );
    }
}

const DeploymentProcessSettings: React.FC = () => {
    const match = useRouteMatch() ?? undefined;
    const projectContext = useProjectContext();
    const dispatchAction = useAnalyticActionDispatch();
    const trackAction = useAnalyticTrackedActionDispatch();

    return <DeploymentProcessSettingsInternal match={match} projectContext={projectContext} dispatchAction={dispatchAction} trackAction={trackAction} />;
};

function displayCalloutIfVersioningConfigurationInvalid(project: ProjectResource, deploymentSettings: DeploymentSettingsResource, deploymentProcess?: DeploymentProcessResource) {
    if (!deploymentProcess) return <></>;
    const actions = flatten(deploymentProcess.Steps.map((step) => step.Actions));
    const callout = isVersioningConfigurationValid(project, deploymentSettings, actions);
    if (callout) {
        return (
            <UnstructuredFormSection stretchContent={true}>
                <Callout type={CalloutType.Warning} title="Invalid Configuration">
                    {callout}
                </Callout>
            </UnstructuredFormSection>
        );
    }
}

export default DeploymentProcessSettings;
